import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import type { User, UserRole, LoginRequest } from '@/lib/api';

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  role: UserRole | null;
  login: (credentials: LoginRequest) => Promise<User>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
}

export const AuthContext = createContext<AuthState | null>(null);

/**
 * Provider hook that manages auth state. Used by the AuthProvider component.
 *
 * Authentication is now cookie-based (httpOnly). On mount we call /auth/me
 * to check if the cookie is valid. Login/logout hit the API which sets/clears
 * the cookie via Set-Cookie headers.
 */
export function useAuthProvider(): AuthState {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // On mount, verify the cookie-based session against the server.
  // Public surfaces (client portal, /start front door) have no broker session —
  // skip the /auth/me probe so it doesn't 401 as console noise.
  useEffect(() => {
    if (window.location.pathname.startsWith('/portal') || window.location.pathname.startsWith('/start')) {
      setUser(null);
      setIsLoading(false);
      return;
    }
    api.auth
      .me()
      .then((serverUser) => {
        setUser(serverUser);
      })
      .catch(() => {
        setUser(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const login = useCallback(async (credentials: LoginRequest): Promise<User> => {
    setIsLoading(true);
    try {
      const response = await api.auth.login(credentials);
      setUser(response.user);
      return response.user;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await api.auth.logout();
    } catch {
      // Even if the API call fails, clear local state
    }
    setUser(null);
    window.location.href = '/login';
  }, []);

  const updateUser = useCallback((updatedUser: User): void => {
    setUser(updatedUser);
  }, []);

  const isAuthenticated = user !== null;
  const role = user?.role ?? null;

  const hasRoleFn = useCallback(
    (r: UserRole): boolean => {
      return user?.role === r;
    },
    [user],
  );

  const hasAnyRoleFn = useCallback(
    (roles: UserRole[]): boolean => {
      return user !== null && roles.includes(user.role);
    },
    [user],
  );

  return useMemo(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      role,
      login,
      logout,
      updateUser,
      hasRole: hasRoleFn,
      hasAnyRole: hasAnyRoleFn,
    }),
    [user, isAuthenticated, isLoading, role, login, logout, updateUser, hasRoleFn, hasAnyRoleFn],
  );
}

/**
 * Hook to access auth context. Must be used within an AuthProvider.
 */
export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
